// =========================
// AUTH.JS
// =========================

// External Libraries
import splash from './views/partials/splash';
import { html, render } from 'lit-html';

// App Modules
import App from './App';
import Utils from './Utils';
import Router, { gotoRoute } from './Router';

// This is the Auth class that handles user authentication
// It includes methods for signing up, signing in, checking authentication status,
class Auth {
  constructor() {
    this.currentUser = {};
  }

  // This method is called when the user signs up
  // It sends a POST request to the server with the user data
  async signUp(userData, fail = false) {
    const response = await fetch(`${App.apiBase}/auth/signup`, {
      method: 'POST',
      body: userData,
    });

    if (!response.ok) {
      const err = await response.json();
      if (typeof fail === 'function') fail();
      throw new Error(err.message || 'Signup failed');
    }

    gotoRoute('/signin');
  }

  // This method is called when the user signs in
  // It sends a POST request to the server with the user data
  async signIn(userData, fail = false) {
    const response = await fetch(`${App.apiBase}/auth/signin`, {
      method: 'POST',
      body: userData,
    });

    if (!response.ok) {
      const err = await response.json();
      if (err) console.log(err);

      if (typeof fail === 'function') fail();

      throw new Error(err.message || 'Invalid email or password');
    }

    const data = await response.json();
    localStorage.setItem('accessToken', data.accessToken);
    this.currentUser = data.user;

    if (this.currentUser.role === 'supplier') {
      try {
        const supplier = await Utils.getSupplierDetails(this.currentUser.id);
        if (supplier?.businessName) {
          this.currentUser.businessName = supplier.businessName;
        }
      } catch (err) {
        console.warn('Failed to fetch supplier data during signIn:', err);
      }
    }

    Router.init();
    gotoRoute('/feed');
  }

  // This method is called to check if the user is logged in
  // It sends a GET request to the server to validate the access token
  async check(success) {
    render(splash, App.rootEl);

    const publicRoutes = ['/', '/signin', '/signup'];
    const currentPath = window.location.pathname;

    const token = localStorage.getItem('accessToken');

    if (!token) {
      this.currentUser = null;
      if (!publicRoutes.includes(currentPath)) {
        gotoRoute('/');
      } else {
        success();
      }
      return;
    }

    try {
      const response = await fetch(`${App.apiBase}/auth/validate`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Token invalid or expired');
      }

      const data = await response.json();
      this.currentUser = data.user;

      if (this.currentUser.role === 'supplier') {
        try {
          const supplier = await Utils.getSupplierDetails(this.currentUser.id);
          if (supplier?.businessName) {
            this.currentUser.businessName = supplier.businessName;
          }
        } catch (err) {
          console.warn('Failed to fetch supplier info in check():', err);
        }
      }

      success();
    } catch (err) {
      console.warn('Auth check failed:', err.message);

      localStorage.removeItem('accessToken');
      this.currentUser = null;

      if (!publicRoutes.includes(currentPath)) {
        gotoRoute('/');
      } else {
        success();
      }
    }
  }

  // This method is called to sign out the user
  // It removes the access token from local storage and redirects to the sign-in page
  signOut() {
    localStorage.removeItem('accessToken');
    gotoRoute('/signin');
    this.currentUser = null;
  }

  // This method checks if the user is logged in
  // It returns true if the access token is present and the current user ID is valid
  // It is used by the landing, sign-in, and sign-up pages to determine if the user is logged in
  isLoggedIn() {
    return !!localStorage.getItem('accessToken') && !!this.currentUser?.id;
  }

  // Function to get the current user from localStorage or API and refresh it
  async refreshCurrentUser() {
    try {
      const res = await fetch(`${App.apiBase}/user/${this.currentUser.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!res.ok) throw new Error('Failed to refresh user');

      const updatedUser = await res.json();
      this.currentUser = updatedUser;
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (err) {
      console.error('[Auth] Error refreshing current user:', err);
    }
  }
}

export default new Auth();
